import { Navigate, Outlet } from "react-router-dom";

import {
  canAccessFeature,
  FeatureId,
  FeatureLevel,
} from "../../auth/featureScopes";
import { useAuth } from "../../contexts/AuthContext";
import useCustomerDetails from "../../data/useCustomerDetails";
import { wilmaSSOAppRegistration, wilmaSSODashboardUrl } from "../../config";
import NoAccess from "../Error/NoAccess";

interface IAllowedRoles {
  isDriver?: boolean;
  isStaff?: boolean;
  isAdmin?: boolean;
  isMechanic?: boolean;
  isExporter?: boolean;
}

export default function RequireAuth({
  allowedRoles,
  requiredFeature,
  requiredFeatureTme,
  minLevel = 1,
}: {
  allowedRoles: IAllowedRoles;
  requiredFeature?: FeatureId;
  /** When TME is enabled, use this feature instead of requiredFeature. */
  requiredFeatureTme?: FeatureId;
  minLevel?: FeatureLevel;
}) {
  const { loading, loggedIn, claims, featureScopes } = useAuth();
  const { features } = useCustomerDetails();

  const waitingForTmeFeature =
    !!requiredFeatureTme && features.isLoading && !features.data;

  const isTme = !!features.data?.data?.some((feature) => feature.name === "TME");
  const effectiveFeature =
    requiredFeatureTme && isTme ? requiredFeatureTme : requiredFeature;

  const hasAllowedRole = () =>
    Object.keys(allowedRoles).some(
      (role) =>
        allowedRoles[role as keyof IAllowedRoles] &&
        claims![role as keyof IAllowedRoles]
    );

  const hasFeatureAccess = () => {
    if (!effectiveFeature) return true;
    return canAccessFeature(featureScopes, effectiveFeature, minLevel);
  };

  if (waitingForTmeFeature) {
    return <></>;
  }

  if (!loading && loggedIn && hasAllowedRole() && hasFeatureAccess()) {
    return <Outlet />;
  } else if (!loading && loggedIn && hasAllowedRole() && !hasFeatureAccess()) {
    return <NoAccess />;
  } else if (!loading && loggedIn && !hasAllowedRole()) {
    return <Navigate to="/" replace />;
  } else if (!loading && !loggedIn) {
    window.location.assign(
      `${wilmaSSODashboardUrl}?ari=${wilmaSSOAppRegistration}&returnUrl=${window.location.href}`
    );
    return <></>;
  } else {
    return <></>;
  }
}
